import * as BaseApi from "api/Base.api";
import { IPagination, ILikeToggleResponse } from "api/interfaces/common.interface";
import { IDiary, IDiaryComment, IDiaryLike } from "api/interfaces/profile.interface";
import { IUser } from "api/interfaces/user.interface";

export interface IDiaryCreateDataResult {
    data: IDiary;
}

// 다이어리 생성
export const createDiary = async (diaryData: {
    title: string;
    content: string;
    imageIds?: string[];
    videoId?: string;
    isPortfolio?: boolean;
}): Promise<IDiaryCreateDataResult> => {
    try {
        const response = await BaseApi.post("/student/v2/diary", diaryData) as any;
        return response.data as IDiaryCreateDataResult;
    } catch (error) {
        console.error("다이어리 생성 실패:", error);
        throw error;
    }
};

export interface IDiaryListResult {
    diaryList: IDiary[];
    pagination: IPagination;
}

export interface IDiaryDetailResult {
    diary: IDiary;
}

export interface IDiaryCommentsResult {
    commentList: IDiaryComment[];
    pagination: IPagination;
}

export interface ICreateCommentRequest {
    contents: string;
    parentId?: number;
}

export interface ICreateCommentResponse {
    comment: IDiaryComment;
}

// 번역 관련 인터페이스
export interface ITranslateResponse {
    translatedTitle: string;
    translatedContent: string;
}

export interface ICommentTranslateResponse {
    translatedContents: string;
}

export interface IDiaryLikesResult {
    likeList: IDiaryLike[];
    castingManagerLikeCount: number;
    pagination: IPagination;
}

// 다이어리 목록 조회
export const getDiaryList = async (params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    userStudentId?: number;
}): Promise<IDiaryListResult> => {
    try {
        const data = await BaseApi.get("/student/v2/diary", params);
        console.log("response", { data });
        return data as IDiaryListResult;
    } catch (error) {
        console.error("다이어리 목록 조회 실패:", error);
        throw error;
    }
};

// 다이어리 상세 조회
export const getDiaryDetail = async (diaryId: string): Promise<IDiaryDetailResult> => {
    try {
        const response = await BaseApi.get(`/student/v2/diary/${diaryId}`);
        return response as IDiaryDetailResult;
    } catch (error) {
        console.error("다이어리 상세 조회 실패:", error);
        throw error;
    }
};

// 다이어리 수정
export const updateDiary = async (diaryId: string, diaryData: {
    title: string;
    content: string;
    imageIds?: string[];
    videoId?: string;
    isPortfolio?: boolean;
}) => {
    try {
        const response = await BaseApi.put(`/student/v2/diary/${diaryId}`, diaryData);
        return response;
    } catch (error) {
        console.error("다이어리 수정 실패:", error);
        throw error;
    }
};

// 다이어리 삭제
export const deleteDiary = async (diaryId: string) => {
    try {
        const response = await BaseApi.remove(`/student/v2/diary/${diaryId}`);
        return response;
    } catch (error) {
        console.error("다이어리 삭제 실패:", error);
        throw error;
    }
};

// 다이어리 통계 조회
export const getDiaryStats = async (params?: {
    startDate?: string;
    endDate?: string;
}) => {
    try {
        const response = await BaseApi.get("/student/v2/diary/stats", params);
        return response;
    } catch (error) {
        console.error("다이어리 통계 조회 실패:", error);
        throw error;
    }
};

// 다이어리 좋아요 토글
export const toggleDiaryLike = async (diaryId: string): Promise<ILikeToggleResponse> => {
    try {
        const response = await BaseApi.post2(`/student/v2/diary/${diaryId}/like`);
        return response as ILikeToggleResponse;
    } catch (error) {
        console.error("다이어리 좋아요 토글 실패:", error);
        throw error;
    }
};

// 다이어리 좋아요 목록 조회
export const getDiaryLikes = async (diaryId: string, params?: {
    page?: number;
    limit?: number;
}): Promise<IDiaryLikesResult> => {
    try {
        const response = await BaseApi.get(`/student/v2/diary/${diaryId}/like`, params);
        return response as IDiaryLikesResult;
    } catch (error) {
        console.error("다이어리 좋아요 목록 조회 실패:", error);
        throw error;
    }
};

// 다이어리 코멘트 목록 조회
export const getDiaryComments = async (diaryId: string, params?: {
    page?: number;
    limit?: number;
}): Promise<IDiaryCommentsResult> => {
    try {
        const response = await BaseApi.get(`/student/v2/diary/${diaryId}/comment`, params);
        return response as IDiaryCommentsResult;
    } catch (error) {
        console.error("다이어리 코멘트 목록 조회 실패:", error);
        throw error;
    }
};

// 다이어리 코멘트 작성
export const createDiaryComment = async (diaryId: string, content: string, parentId?: number): Promise<ICreateCommentResponse> => {
    try {
        const data: ICreateCommentRequest = {
            contents: content,
            parentId: parentId
        };
        const response = await BaseApi.post2(`/student/v2/diary/${diaryId}/comment`, data);
        return response as ICreateCommentResponse;
    } catch (error) {
        console.error("다이어리 코멘트 작성 실패:", error);
        throw error;
    }
};

// 다이어리 코멘트 삭제
export const deleteDiaryComment = async (diaryId: string, commentId: number): Promise<void> => {
    try {
        await BaseApi.remove(`/student/v2/diary/${diaryId}/comment/${commentId}`);
    } catch (error) {
        console.error("다이어리 코멘트 삭제 실패:", error);
        throw error;
    }
};

// 다이어리 코멘트 좋아요 토글
export const toggleCommentLike = async (diaryId: string, commentId: number): Promise<ILikeToggleResponse> => {
    try {
        const response = await BaseApi.post2(`/student/v2/diary/${diaryId}/comment/${commentId}/like`);
        return response as ILikeToggleResponse;
    } catch (error) {
        console.error("다이어리 코멘트 좋아요 토글 실패:", error);
        throw error;
    }
};

export interface IHiddenToggleResponse {
    isHidden: boolean;
    message: string;
}

// 다이어리 감추기 토글
export const toggleDiaryHidden = async (diaryId: string): Promise<IHiddenToggleResponse> => {
    try {
        const response = await BaseApi.post2(`/student/v2/diary/${diaryId}/hidden`);
        return response as IHiddenToggleResponse;
    } catch (error) {
        console.error("다이어리 감추기 토글 실패:", error);
        throw error;
    }
};

export interface ISpecificDiaryStatsResult {
    diaryId: number; // diary Id
    title: string; // diary title
    createdAt: string; // diary created at
    stats: ISpecificDiaryStats;
}

export interface ISpecificDiaryStatsGrowth {
    after24h: number; // 24시간 동안
    after7d: number; // 7일 동안
    after30d: number; // 30일 동안
    percentile: string;
}

export interface ISpecificDiaryStatsBookmarkGrowth {
    after24h: number; // 24시간 동안
    after7d: number; // 7일 동안
    after30d: number; // 30일 동안
    percentile: string;
}

export interface ISpecificDiaryStats {
    totalViews: number;
    scrollViews: number;
    detailViews: number;
    totalBookmarks: number;
    visitedCastingManagers: number;
    viewGrowth: ISpecificDiaryStatsGrowth;
    bookmarkGrowth: ISpecificDiaryStatsBookmarkGrowth;
}


// 다이어리 통계 조회
export const getSpecificDiaryStats = async (diaryId: string): Promise<ISpecificDiaryStatsResult> => {
    try {
        const response = await BaseApi.get(`/student/v2/diary/${diaryId}/stats`);
        return response as ISpecificDiaryStatsResult;
    } catch (error) {
        console.error("다이어리 통계 조회 실패:", error);
        throw error;
    }
};

// 다이어리 번역
export const translateDiary = async (diaryId: string): Promise<ITranslateResponse> => {
    try {
        const response = await BaseApi.post2(`/student/v2/diary/${diaryId}/translate`);
        console.log("다이어리 번역 성공:", response);
        return response as ITranslateResponse;
    } catch (error) {
        console.error("다이어리 번역 실패:", error);
        throw error;
    }
};

// 다이어리 코멘트 번역
export const translateDiaryComment = async (diaryId: string, commentId: string): Promise<ICommentTranslateResponse> => {
    try {
        const response = await BaseApi.post2(`/student/v2/diary/${diaryId}/comment/${commentId}/translate`);
        console.log("다이어리 코멘트 번역 성공:", response);
        return response as ICommentTranslateResponse;
    } catch (error) {
        console.error("다이어리 코멘트 번역 실패:", error);
        throw error;
    }
};